import ReplayIcon from '@mui/icons-material/Replay'
import { Autocomplete, Box, Button, Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateMainComponentCache, useGetEquipment, useGetMainComponentById } from 'src/api/main-component'
import { useGetAllMooringsByLocalityId } from 'src/api/moorings'
import { invalidatePositionCache } from 'src/api/positions'
import { useCreateROVCategory } from 'src/api/rov-category'
import {
  useCreateROVVideo,
  useCreateVimeoPlaceholder,
  useGetROVVideoUploadProvider,
  useInitializeAzureBlobUpload,
} from 'src/api/rov-videos'
import { ReactComponent as FileUploadIcon } from 'src/assets/svg/fileUploadIcon.svg'
import { AppTextField } from 'src/components/AppTextField'
import { DragAndDropBox } from 'src/components/common/input/CommonFileUploader'
import { VideoFramePickerDialog } from 'src/components/VideoFramePickerDialog'
import { IROVVideoCategory, IROVVideosWithSharedCategories } from 'src/generated-types/rovvideo'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { ResumableBlobUpload } from 'src/utils/azureBlobUpload'
import { getPositionName } from 'src/utils/function'
import { captureVideoScreenshot } from 'src/utils/rovVideoScreenshot'
import { isRovVideoFile, ROV_VIDEO_DROPZONE_ACCEPT } from 'src/utils/rovVideoFile'
import { getTranslatedText } from 'src/utils/translations'
import { ResumableVimeoUpload } from 'src/utils/vimeoUpload'
import { GenericDialogLayout } from '../../../../components/GenericDialogLayout'
import { MainButton } from '../../../../components/MainButton'
import { ShowDialogProps } from '../../../../show-dialog'
import { VideoRow } from './BulkUploadRovDialog.VideoRow'
import { DialogContentBox } from './BulkUploadRovDialog.style'
import {
  applyManualScreenshotSelection,
  createConcurrencyLimiter,
  createVideoEntry,
  MAX_CONCURRENT_UPLOADS,
  processBlobVideo,
  runWithConcurrency,
  VideoEntry,
} from './BulkUploadRovDialog.action'
import { processVimeoVideo } from './BulkUploadRovDialog.vimeo.action'
import { getUniqueCategories } from './UploadRovDialog.action'

export interface BulkUploadRovDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  mooringId?: EntityId
  positionId?: EntityId
  mainComponentId?: EntityId
  equipmentOnly?: boolean
  rovVideosWithSharedCategories?: IROVVideosWithSharedCategories
  selectedCategoryId?: EntityId | null
}

export const BulkUploadRovDialog: React.FC<BulkUploadRovDialogProps> = ({
  pop,
  customerId,
  localityId,
  mooringId: initialMooringId,
  positionId: initialPositionId,
  mainComponentId: initialMainComponentId,
  equipmentOnly = false,
  rovVideosWithSharedCategories,
  selectedCategoryId,
}) => {
  const { t } = useTranslation(['document', 'common', 'rov', 'position'])
  const { showDialog } = useDialogCtx()
  const theme = useTheme()
  const [videos, setVideos] = useState<VideoEntry[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [category, setCategory] = useState<IROVVideoCategory | null>(null)
  const [isShared, setIsShared] = useState(false)

  const resumableUploadsRef = useRef<Map<string, ResumableBlobUpload | ResumableVimeoUpload>>(new Map())
  const screenshotPromisesRef = useRef<Map<string, Promise<Blob | null>>>(new Map())
  const screenshotLimiterRef = useRef(createConcurrencyLimiter(MAX_CONCURRENT_UPLOADS))

  const videosRef = useRef<VideoEntry[]>([])
  useEffect(() => {
    videosRef.current = videos
  }, [videos])

  const initializeBlobUploadMutation = useInitializeAzureBlobUpload(customerId, localityId)
  const createVimeoPlaceholderMutation = useCreateVimeoPlaceholder(customerId, localityId)
  const createROVVideoMutation = useCreateROVVideo(customerId, localityId)
  const createROVCategoryMutation = useCreateROVCategory(customerId, localityId)
  const uploadProviderQuery = useGetROVVideoUploadProvider(customerId, localityId)
  const useAzureBlob = uploadProviderQuery.data?.useAzureBlob ?? true
  const isProviderLoading = uploadProviderQuery.isLoading

  const { data: moorings } = useGetAllMooringsByLocalityId(customerId, localityId)

  // When opened from equipment context, fetch only that equipment; otherwise fetch all for locality
  const { data: allEquipment = [] } = useGetEquipment(customerId, { localityId })
  const { data: singleEquipment } = useGetMainComponentById(customerId, (initialMainComponentId ?? 0) as EntityId)
  const equipment = (equipmentOnly ? (singleEquipment ? [singleEquipment] : []) : [...allEquipment]).sort((a, b) => {
    const cageA = a.position ? getPositionName(a.position) : ''
    const cageB = b.position ? getPositionName(b.position) : ''
    const typeA = getTranslatedText(a.type?.name) || a.typeName || ''
    const typeB = getTranslatedText(b.type?.name) || b.typeName || ''
    return (
      cageA.localeCompare(cageB) ||
      typeA.localeCompare(typeB) ||
      (a.serialNumber || '').localeCompare(b.serialNumber || '')
    )
  })

  const categories = getUniqueCategories(rovVideosWithSharedCategories)

  // Initialize category from selectedCategoryId
  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const found = categories.find(c => c.id === selectedCategoryId)
      if (found) setCategory(found)
    }
  }, [selectedCategoryId, categories])

  // Abort any running uploads and revoke any preview object URLs on unmount
  useEffect(() => {
    const uploads = resumableUploadsRef.current
    return () => {
      uploads.forEach(upload => upload.pause())
      uploads.clear()
      videosRef.current.forEach(v => {
        if (v.screenshotPreviewUrl) URL.revokeObjectURL(v.screenshotPreviewUrl)
      })
    }
  }, [])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const videoFiles = acceptedFiles.filter(isRovVideoFile)
      if (videoFiles.length !== acceptedFiles.length) {
        toast.warning(t('some-files-not-video', { ns: 'rov' }))
      }

      const newEntries: VideoEntry[] = videoFiles.map(file =>
        createVideoEntry(file, {
          mooringId: initialMainComponentId ? null : initialMooringId ?? null,
          positionId: initialMainComponentId ? null : initialPositionId ?? null,
          mainComponentId: initialMainComponentId ?? null,
        }),
      )

      if (useAzureBlob) {
        newEntries.forEach(entry => {
          const capturePromise = screenshotLimiterRef.current(() => captureVideoScreenshot(entry.file))
          screenshotPromisesRef.current.set(entry.id, capturePromise)
          capturePromise.then(blob => {
            setVideos(prev =>
              prev.map(v =>
                v.id === entry.id ? { ...v, screenshotPreviewUrl: blob ? URL.createObjectURL(blob) : null } : v,
              ),
            )
          })
        })
      }

      setVideos(prev => [...prev, ...newEntries])
    },
    [initialMooringId, initialPositionId, initialMainComponentId, useAzureBlob, t],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: ROV_VIDEO_DROPZONE_ACCEPT,
  })

  const updateVideo = (id: string, updates: Partial<VideoEntry>) => {
    setVideos(prev => prev.map(v => (v.id === id ? { ...v, ...updates } : v)))
  }

  const removeVideo = (id: string) => {
    resumableUploadsRef.current.get(id)?.pause()
    resumableUploadsRef.current.delete(id)
    screenshotPromisesRef.current.delete(id)
    const removed = videosRef.current.find(v => v.id === id)
    if (removed?.screenshotPreviewUrl) URL.revokeObjectURL(removed.screenshotPreviewUrl)
    setVideos(prev => prev.filter(v => v.id !== id))
  }

  const handlePickFrame = (videoId: string) => {
    const video = videosRef.current.find(v => v.id === videoId)
    if (!video) return

    showDialog(
      VideoFramePickerDialog,
      {
        componentProps: { file: video.file },
        opts: { maxWidth: 'md', fullWidth: true },
        pop: (result?: { blob: Blob }) => {
          if (result?.blob) {
            applyManualScreenshotSelection(videoId, result.blob, {
              videos: videosRef.current,
              screenshotPromisesRef,
              updateVideo,
            })
          }
        },
      },
      undefined,
      true,
    )
  }

  const pauseVideo = (id: string) => {
    const upload = resumableUploadsRef.current.get(id)
    if (!upload) return

    upload.pause()
    updateVideo(id, { status: 'paused' })
  }

  const resumeVideo = (id: string) => {
    const upload = resumableUploadsRef.current.get(id)
    if (!upload) return

    updateVideo(id, { status: 'uploading' })
    upload.resume()
  }

  const invalidateCachesForVideo = (video: VideoEntry) => {
    if (video.positionId && video.mooringId) {
      invalidatePositionCache.getPositionROVVideos(
        customerId,
        localityId,
        video.mooringId as EntityId,
        video.positionId,
      )
    }
    if (video.mainComponentId) {
      invalidateMainComponentCache.getMainComponentROVVideos(customerId, video.mainComponentId as EntityId)
    }
  }

  const processVideo = async (video: VideoEntry, categoryOverride?: IROVVideoCategory | null) => {
    const effectiveCategory = categoryOverride !== undefined ? categoryOverride : category

    if (useAzureBlob) {
      await processBlobVideo(video, {
        category: effectiveCategory,
        isShared,
        customerId,
        localityId,
        screenshotPromise: screenshotPromisesRef.current.get(video.id) ?? Promise.resolve(null),
        initializeBlobUpload: input => initializeBlobUploadMutation.mutateAsync(input),
        createROVVideo: input => createROVVideoMutation.mutateAsync(input as any),
        updateVideo,
        resumableUploadsRef,
        onSuccess: invalidateCachesForVideo,
      })
    } else {
      await processVimeoVideo(video, {
        category: effectiveCategory,
        isShared,
        customerId,
        createVimeoPlaceholder: input => createVimeoPlaceholderMutation.mutateAsync(input),
        createROVVideo: input => createROVVideoMutation.mutateAsync(input as any),
        updateVideo,
        resumableUploadsRef,
        onSuccess: invalidateCachesForVideo,
      })
    }
  }

  const handleSubmit = async () => {
    const validVideos = videos.filter(v => v.status === 'pending')

    if (validVideos.length === 0) {
      toast.warning(t('no-videos-to-upload', { ns: 'rov' }))
      return
    }

    setIsSubmitting(true)

    // Pre-create a new category once before all uploads
    let resolvedCategory = category
    if (category?.id === 0 && category.categoryName?.trim()) {
      try {
        const result = await createROVCategoryMutation.mutateAsync({
          categoryName: category.categoryName,
          isShared,
        })
        resolvedCategory = result.data as IROVVideoCategory
        setCategory(resolvedCategory)
      } catch {
        toast.error(t('failed-to-create-category', { ns: 'rov' }))
        setIsSubmitting(false)
        return
      }
    }

    await runWithConcurrency(validVideos, MAX_CONCURRENT_UPLOADS, v => processVideo(v, resolvedCategory))

    invalidationActivityLogCache.getCageActivityLogs()
    setIsSubmitting(false)
    checkCompletion()
  }

  const handleRetry = async (videoId: string) => {
    const video = videos.find(v => v.id === videoId)
    if (!video || video.status !== 'error') return

    setIsSubmitting(true)
    await processVideo(video)
    invalidationActivityLogCache.getCageActivityLogs()
    setIsSubmitting(false)
    checkCompletion()
  }

  const handleRetryAll = async () => {
    const failedVideos = videos.filter(v => v.status === 'error')
    if (failedVideos.length === 0) return

    setIsSubmitting(true)

    await runWithConcurrency(failedVideos, MAX_CONCURRENT_UPLOADS, v => processVideo(v))

    invalidationActivityLogCache.getCageActivityLogs()
    setIsSubmitting(false)
    checkCompletion()
  }

  const checkCompletion = () => {
    const current = videosRef.current
    const hasErrors = current.some(v => v.status === 'error')
    const hasPending = current.some(v => v.status === 'pending')

    if (!hasErrors && !hasPending && current.length > 0) {
      toast.success(t('bulk-upload-success', { ns: 'rov' }))
      setTimeout(() => pop(), 300)
    } else if (hasErrors) {
      toast.warning(t('bulk-upload-partial', { ns: 'rov' }))
    }
  }

  const handleClose = () => {
    if (isSubmitting || videos.some(v => v.status === 'uploading' || v.status === 'paused')) {
      toast.warning(t('upload-in-progress', { ns: 'common' }))
      return
    }
    pop()
  }

  const pendingVideos = videos.filter(v => v.status === 'pending')
  const failedVideos = videos.filter(v => v.status === 'error')
  const pendingCount = pendingVideos.length
  const failedCount = failedVideos.length
  const allPendingHaveTarget = pendingVideos.every(v => v.positionId || v.mainComponentId)
  const canUpload = !isSubmitting && !isProviderLoading && pendingCount > 0 && allPendingHaveTarget
  const canRetry = !isSubmitting && !isProviderLoading && failedCount > 0

  const isCategoryFromExisting = category?.id && category.id !== 0
  const isShareDisabled =
    !category || !category.categoryName || category.categoryName.trim() === '' || isCategoryFromExisting

  return (
    <GenericDialogLayout
      title={t('bulk-upload-rov', { ns: 'rov' })}
      pop={handleClose}
      removedCloseButton={isSubmitting}
      actions={[
        ...(failedCount > 0
          ? [
              <Button
                disabled={!canRetry}
                onClick={handleRetryAll}
                key="retry-all-btn"
                variant="outlined"
                startIcon={<ReplayIcon />}
                sx={{ mr: 1 }}
              >
                {t('retry-all-failed', { ns: 'rov', count: failedCount })}
              </Button>,
            ]
          : []),
        <MainButton disabled={!canUpload} onClick={handleSubmit} key="bulk-upload-btn" data-cy="bulkUploadROV">
          {isSubmitting ? t('uploading', { ns: 'common' }) : t('upload-all', { ns: 'rov', count: pendingCount })}
        </MainButton>,
      ]}
    >
      <DialogContentBox isSubmitting={isSubmitting}>
        {/* Category and share controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: '50%' }}>
            <Autocomplete
              freeSolo
              options={categories}
              value={category}
              disabled={isSubmitting}
              getOptionLabel={option => {
                if (typeof option === 'string') return option
                return option?.categoryName || ''
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false
                return option.id === value.id
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setCategory({ id: 0 as EntityId, categoryName: newValue, isShared: false })
                } else {
                  setCategory(newValue)
                  if (newValue?.isShared) setIsShared(true)
                }
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setCategory({ id: 0 as EntityId, categoryName: newInputValue, isShared: false })
                } else if (reason === 'clear') {
                  setCategory(null)
                  setIsShared(false)
                }
              }}
              renderInput={params => <AppTextField {...params} label={t('category', { ns: 'common' })} fullWidth />}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isShared}
                onChange={e => setIsShared(e.target.checked)}
                disabled={!!isShareDisabled || isSubmitting}
              />
            }
            label={t('share', { ns: 'common' }) as string}
          />
        </Box>

        {/* Drop zone */}
        <DragAndDropBox
          height="160px"
          data-cy="rov-bulk-dropzone"
          {...getRootProps({ disabled: isSubmitting })}
          sx={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
        >
          <input {...getInputProps()} />
          <Flex.Row justifyContent="center">
            <Flex.Column justifyContent="center" alignItems="center" spacing={0.3}>
              <FileUploadIcon />
              <Typography
                variant="subtitle1"
                fontWeight={500}
                color={getThemeColor(theme, MUIThemeColors.primaryDark)}
                textAlign="center"
              >
                {t('dragndrop-title', { ns: 'file-upload' })}
              </Typography>
              <Typography variant="subtitle2" fontWeight={400} textAlign="center">
                {t('drop-multiple-videos', { ns: 'rov' })}
              </Typography>
            </Flex.Column>
          </Flex.Row>
        </DragAndDropBox>

        {/* Video list */}
        {videos.length > 0 && (
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {videos.map(video => (
              <VideoRow
                key={video.id}
                video={video}
                customerId={customerId}
                localityId={localityId}
                moorings={moorings ?? []}
                equipment={equipment}
                equipmentOnly={equipmentOnly}
                onUpdate={updateVideo}
                onRemove={removeVideo}
                onRetry={handleRetry}
                onPause={pauseVideo}
                onResume={resumeVideo}
                isSubmitting={isSubmitting}
                screenshotPreviewUrl={video.screenshotPreviewUrl}
                onPickFrame={handlePickFrame}
                showScreenshotPicker={useAzureBlob}
              />
            ))}
          </Box>
        )}
      </DialogContentBox>
    </GenericDialogLayout>
  )
}
